import React, { useState, useEffect } from "react";
import axios from "axios";
import "./BusinessDashboard.css";
import Switch from "@mui/material/Switch";
import { API_URL } from "../../services/api";
import { useToast } from "../Toast";
import { Loader2 } from "lucide-react";
import { HelpCircle } from "lucide-react";

const BusinessDashboard = () => {
  const { showToast, ToastContainer } = useToast();
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState("orders");
  const [selectedBusinessDetails, setSelectedBusinessDetails] = useState(null);
  const [orderSyncEnabled, setOrderSyncEnabled] = useState(false);
  //const [orderSyncEnabled, setOrderSyncEnabled] = useState(selectedBusinessDetails?.shopify_order_sync || false);
  const [isShopifySyncModalOpen, setIsShopifySyncModalOpen] = useState(false);
  const [clearWithSync, setClearWithSync] = useState(false);
  const [manualAccessToken, setManualAccessToken] = useState("");
  const [manualShopifyUrl, setManualShopifyUrl] = useState("");
  const [showHelpModal, setShowHelpModal] = useState(false);

  const [shopifyUrl, setShopifyUrl] = useState("");

  const [loading, setLoading] = useState(true);

  const [productData, setProductData] = useState({
    name: "",
    description: "",
    price: "",
    condition: "new",
    category: "Eco-Friendly Business",
    product_category: "",
    // Change from single image to array of images
    images: [],
    // Keep track of existing images from the server
    existingImages: [],
    variants: [],
  });

  const [editProductId, setEditProductId] = useState(null);
  const [isEditFormVisible, setIsEditFormVisible] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    productId: null,
  });

  const [isOrderEditModalOpen, setIsOrderEditModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [status, setStatus] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [notes, setNotes] = useState("");

  const statuses = [
    "Pending",
    "Paid",
    "Shipped",
    "Delivered",
    "Refunded",
    "Canceled",
  ];

  const openOrderEditModal = (order) => {
    // console.log("Opening modal for order:", order);
    setSelectedOrder(order);
    setStatus(order.status || "");
    setTrackingNumber(order.tracking_number || "");
    setNotes(order.notes || "");
    setIsOrderEditModalOpen(true);
  };

  const closeOrderEditModal = () => {
    setIsOrderEditModalOpen(false);
    setSelectedOrder(null);
  };

  const [shippingFees, setShippingFees] = useState(null);
  // const [newFees, setNewFees] = useState({
  //     worldwide_shipping_fee: '',
  //     all_except_selected_fee: '',
  //     country_code: '',
  //     country_fee: ''
  // });

  const [newFees, setNewFees] = useState({
    worldwide_shipping_fee: "",
    all_except_selected_fee: "",
    threshold_free: "",
    country_fees: [],
  });

  const [newCountryFee, setNewCountryFee] = useState({
    country_code: "",
    country_fee: "",
  });

  const handleInputChange = (field) => (event) => {
    setNewFees({
      ...newFees,
      [field]: event.target.value,
    });
  };

  // Fetch businesses on component mount

  useEffect(() => {
    const token = localStorage.getItem("access_token"); // Get the token from localStorage
    // console.log("Token in LocalStorage:", token);

    // Only send the request if the token exists
    if (token) {
      setLoading(true);
      axios
        .get(`${API_URL}api/business/dashboard/`, {
          headers: {
            Authorization: `Bearer ${token}`, // Add Authorization header with Bearer token
          },
          withCredentials: true,
        })
        .then((response) => {
          setBusinesses(response.data.businesses);
        })
        .catch((err) => {
          setError(
            err.response
              ? err.response.data.error
              : "Something went wrong while fetching businesses"
          );
        })

        .finally(() => {
          setLoading(false); // Set loading to false after the request completes
        });
    } else {
      // console.log("No access token found in localStorage");
      // Optionally handle case where token is missing, e.g., redirect to login
      window.location.href = "/logout/";
    }
  }, []);

  useEffect(() => {
    if (selectedBusinessDetails) {
      setOrderSyncEnabled(selectedBusinessDetails.shopify_order_sync || false);
    }
  }, [selectedBusinessDetails]);

  const fetchProducts = (businessId) => {
    const token = localStorage.getItem("access_token");

    axios
      .get(`${API_URL}api/business/products/${businessId}/`, {
        headers: {
          Authorization: `Bearer ${token}`, // Add Authorization header with Bearer token
        },
      })
      .then((response) => {
        setProducts(response.data);
      })
      .catch((err) => {
        setError(
          err.response
            ? err.response.data.error
            : "Something went wrong while fetching products"
        );
        setProducts([]);
      });
  };

  const handleBusinessSelect = (businessId) => {
    setSelectedBusiness(businessId);
    setActiveView("orders");
    setLoading(true);
    // console.log("Setting loading to true");
    const token = localStorage.getItem("access_token"); // Get the token from localStorage

    // Fetch business overview for the selected business
    axios
      .get(`${API_URL}api/business/overview/${businessId}/`, {
        headers: {
          Authorization: `Bearer ${token}`, // Add Authorization header with Bearer token
        },
      })
      .then((response) => {
        setSelectedBusinessDetails(response.data);
      })
      .catch((err) => {
        setError(
          err.response
            ? err.response.data.error
            : "Something went wrong while fetching business overview"
        );
        setSelectedBusinessDetails(null);
      });

    // Fetch orders for the selected business
    axios
      .get(`${API_URL}api/business/orders/${businessId}/`, {
        headers: {
          Authorization: `Bearer ${token}`, // Add Authorization header with Bearer token
        },
      })
      .then((response) => {
        setOrders(response.data);
      })
      .catch((err) => {
        setError(
          err.response
            ? err.response.data.error
            : "Something went wrong while fetching orders"
        );
        setOrders([]);
      });

    // Fetch products for the selected business
    axios
      .get(`${API_URL}api/business/products/${businessId}/`, {
        headers: {
          Authorization: `Bearer ${token}`, // Add Authorization header with Bearer token
        },
      })
      .then((response) => {
        setProducts(response.data);
        setLoading(false);
        // console.log("Setting loading to false");
      })
      .catch((err) => {
        setError(
          err.response
            ? err.response.data.error
            : "Something went wrong while fetching products"
        );
        setProducts([]);
      });
  };

  const handleProductChange = (e) => {
    const { name, value } = e.target;
    setProductData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleOrderEditSubmit = async () => {
    try {
      const response = await axios.patch(
        `${API_URL}api/business/orders/edit/${selectedOrder.id}/`,
        {
          status,
          tracking_number: trackingNumber,
          notes,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      if (response.status === 200) {
        // alert("Order updated successfully");
        showToast({
          message: "Order Saved successfully!",
          type: "success",
          duration: 4000, // 5 seconds
        });
        const updatedOrders = orders.map((order) =>
          order.id === selectedOrder.id ? response.data : order
        );
        setOrders(updatedOrders);
        closeOrderEditModal();
      }
    } catch (error) {
      console.error("Error updating order:", error);
      alert("Failed to update order.");
    }
  };

  // Stuff for variants

  const handleAddVariant = () => {
    setProductData((prevData) => ({
      ...prevData,
      variants: [...prevData.variants, { option1: "", price: "", image: null }],
    }));
  };

  const handleVariantChange = (e, index) => {
    const { name, value } = e.target;
    const updatedVariants = [...productData.variants];
    updatedVariants[index][name] = value;
    setProductData({ ...productData, variants: updatedVariants });
  };

  const handleRemoveVariant = (index) => {
    const updatedVariants = productData.variants.filter((_, i) => i !== index);
    setProductData((prevData) => ({
      ...prevData,
      variants: updatedVariants,
    }));
  };

  // Update the state structure to handle multiple images

  // Function to load product data when editing
  const loadProductData = (product) => {
    setProductData({
      name: product.name,
      description: product.description,
      price: product.price,
      condition: product.condition,
      category: product.category || "Eco-Friendly Business",
      product_category: product.product_category,
      images: [], // New images to upload
      existingImages: product.images || [], // Existing images from the server
      variants: product.variants.map((variant) => ({
        ...variant,
        images: [], // New images to upload for this variant
        existingImages: variant.images || [], // Existing variant images
      })),
    });

    setEditProductId(product.id);
    setIsEditFormVisible(true);
  };

  // Handle product image changes (multiple files)
  // const handleProductImageChange = (e) => {
  //     if (e.target.files && e.target.files.length > 0) {
  //         // Convert FileList to array and add to existing images
  //         const newImages = Array.from(e.target.files);
  //         setProductData(prev => ({
  //             ...prev,
  //             images: [...prev.images, ...newImages]
  //         }));
  //     }
  // };
  const handleProductImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      // Convert FileList to array
      const newImages = Array.from(e.target.files);

      setProductData((prev) => {
        // Make sure prev.images is an array before spreading
        const existingImages = Array.isArray(prev.images) ? prev.images : [];

        return {
          ...prev,
          images: [...existingImages, ...newImages],
        };
      });
    }
  };

  // Remove a product image (either new or existing)
  const handleRemoveProductImage = (index, isExisting = false) => {
    setProductData((prev) => {
      if (isExisting) {
        const updatedExistingImages = [...prev.existingImages];
        updatedExistingImages.splice(index, 1);
        return { ...prev, existingImages: updatedExistingImages };
      } else {
        const updatedImages = [...prev.images];
        updatedImages.splice(index, 1);
        return { ...prev, images: updatedImages };
      }
    });
  };

  // Handle variant image changes (multiple files)
  // const handleVariantImageChange = (e, variantIndex) => {
  //     if (e.target.files && e.target.files.length > 0) {
  //         const newImages = Array.from(e.target.files);
  //         setProductData(prev => {
  //             const updatedVariants = [...prev.variants];
  //             updatedVariants[variantIndex] = {
  //                 ...updatedVariants[variantIndex],
  //                 images: [...(updatedVariants[variantIndex].images || []), ...newImages]
  //             };
  //             return { ...prev, variants: updatedVariants };
  //         });
  //     }
  // };
  const handleVariantImageChange = (e, variantIndex) => {
    if (e.target.files && e.target.files.length > 0) {
      // Convert FileList to array
      const newImages = Array.from(e.target.files);

      setProductData((prev) => {
        const updatedVariants = [...prev.variants];
        const existingVariantImages = Array.isArray(
          updatedVariants[variantIndex].images
        )
          ? updatedVariants[variantIndex].images
          : [];

        updatedVariants[variantIndex] = {
          ...updatedVariants[variantIndex],
          images: [...existingVariantImages, ...newImages],
        };

        return { ...prev, variants: updatedVariants };
      });
    }
  };

  // Remove a variant image (either new or existing)
  const handleRemoveVariantImage = (
    variantIndex,
    imageIndex,
    isExisting = false
  ) => {
    setProductData((prev) => {
      const updatedVariants = [...prev.variants];
      if (isExisting) {
        const existingImages = [
          ...(updatedVariants[variantIndex].existingImages || []),
        ];
        existingImages.splice(imageIndex, 1);
        updatedVariants[variantIndex] = {
          ...updatedVariants[variantIndex],
          existingImages,
        };
      } else {
        const images = [...(updatedVariants[variantIndex].images || [])];
        images.splice(imageIndex, 1);
        updatedVariants[variantIndex] = {
          ...updatedVariants[variantIndex],
          images,
        };
      }
      return { ...prev, variants: updatedVariants };
    });
  };

  // Updated form submission function
  // const handleProductSubmit = (e) => {
  //     e.preventDefault();

  //     if (!selectedBusiness) {
  //         alert('Please select a business before adding or editing a product.');
  //         return;
  //     }

  //     const formData = new FormData();
  //     formData.append('name', productData.name);
  //     formData.append('description', productData.description);
  //     formData.append('price', Number(productData.price));
  //     formData.append('condition', productData.condition);
  //     formData.append('category', productData.category);
  //     formData.append('product_category', productData.product_category);

  //     // Add new product images
  //     productData.images.forEach((image, index) => {
  //         formData.append(`images[${index}].image`, image);
  //     });

  //     // Keep track of existing images (IDs)
  //     productData.existingImages.forEach((existingImage, index) => {
  //         if (existingImage.id) {
  //             formData.append(`existingImages[${index}]`, existingImage.id);
  //         }
  //     });

  //     // Handle variants, including images
  //     productData.variants.forEach((variant, variantIndex) => {
  //         formData.append(`variants[${variantIndex}].option1`, variant.option1);
  //         formData.append(`variants[${variantIndex}].price`, variant.price);

  //         // Add optional fields if they exist
  //         if (variant.option2) {
  //             formData.append(`variants[${variantIndex}].option2`, variant.option2);
  //         }

  //         // Add variant ID if it exists (for editing)
  //         if (variant.id) {
  //             formData.append(`variants[${variantIndex}].id`, variant.id);
  //         }

  //         // Add new variant images
  //         if (variant.images && variant.images.length > 0) {
  //             variant.images.forEach((image, imageIndex) => {
  //                 formData.append(`variants[${variantIndex}].images[${imageIndex}].image`, image);
  //             });
  //         }

  //         // Keep track of existing variant images
  //         if (variant.existingImages && variant.existingImages.length > 0) {
  //             variant.existingImages.forEach((existingImage, imageIndex) => {
  //                 if (existingImage.id) {
  //                     formData.append(`variants[${variantIndex}].existingImages[${imageIndex}]`, existingImage.id);
  //                 }
  //             });
  //         }
  //     });
  const handleProductSubmit = (e) => {
    e.preventDefault();
    if (!selectedBusiness) {
      alert("Please select a business before adding or editing a product.");
      return;
    }
    const formData = new FormData();
    formData.append("name", productData.name);
    formData.append("description", productData.description);
    formData.append("price", Number(productData.price));
    formData.append("condition", productData.condition);
    formData.append("category", productData.category);
    formData.append("product_category", productData.product_category);

    // Only process images if there are new ones or clearImages flag is set
    // if (productData.clearImages) {
    //     formData.append('clear_images', 'true');
    // } else if (Array.isArray(productData.images) && productData.images.length > 0) {
    //     const newImageFiles = productData.images.filter((img) => img instanceof File);
    //     newImageFiles.forEach((image, index) => {
    //         formData.append(`images[${index}].image`, image);
    //     });

    if (productData.clearImages) {
      formData.append("clear_images", "true");
    }

    if (Array.isArray(productData.images)) {
      const newImages = productData.images.filter((img) => img instanceof File);
      newImages.forEach((image, index) => {
        formData.append(`images[${index}].image`, image);
        // Optional: Add alt text support here if needed
        // formData.append(`images[${index}].alt_text`, image.alt_text || '');
      });

      // Only include existing images if they have valid IDs
      if (
        productData.existingImages &&
        Array.isArray(productData.existingImages) &&
        productData.existingImages.length > 0
      ) {
        productData.existingImages.forEach((existingImage, index) => {
          if (existingImage && existingImage.id) {
            formData.append(`existingImages[${index}]`, existingImage.id);
          }
        });
      }
    }

    // Handle variants with null check and only include valid data
    if (productData.variants && Array.isArray(productData.variants)) {
      productData.variants.forEach((variant, variantIndex) => {
        // Only add variant if it has required data
        if (variant.option1 || variant.price) {
          formData.append(
            `variants[${variantIndex}].option1`,
            variant.option1 || ""
          );
          formData.append(
            `variants[${variantIndex}].price`,
            variant.price || ""
          );

          if (variant.option2) {
            formData.append(
              `variants[${variantIndex}].option2`,
              variant.option2
            );
          }
          //test
          if (variant.id) {
            formData.append(`variants[${variantIndex}].id`, variant.id);
          }

          // Only process variant images if there are new ones or clearImages flag is set
          if (variant.clearImages) {
            formData.append(`variants[${variantIndex}].clear_images`, "true");
          } else if (
            Array.isArray(variant.images) &&
            variant.images.length > 0
          ) {
            const variantImageFiles = variant.images.filter(
              (img) => img instanceof File
            );
            variantImageFiles.forEach((image, imageIndex) => {
              formData.append(
                `variants[${variantIndex}].images[${imageIndex}].image`,
                image
              );
            });

            // Only include existing variant images if they have valid IDs
            if (
              variant.existingImages &&
              Array.isArray(variant.existingImages) &&
              variant.existingImages.length > 0
            ) {
              variant.existingImages.forEach((existingImage, imageIndex) => {
                if (existingImage && existingImage.id) {
                  formData.append(
                    `variants[${variantIndex}].existingImages[${imageIndex}]`,
                    existingImage.id
                  );
                }
              });
            }
          }
        }
      });
    }

    // Log form data for debugging
    // formData.forEach((value, key) => {
    //   console.log(key, value);
    // });

    if (editProductId) {
      setLoading(true);
      axios
        .put(`${API_URL}api/products/${editProductId}/edit/`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          withCredentials: true,
        })
        .then((response) => {
          //alert("Product updated successfully!");
          fetchProducts(selectedBusiness);
          setIsEditFormVisible(false);
          setEditProductId(null);

          setLoading(false);

          resetProductForm();

          showToast({
            message: "Product saved successfully!",
            type: "success",
            duration: 5000, // 5 seconds
          });
        })
        .catch((err) => {
          console.error(
            "Error updating product:",
            err.response ? err.response.data : "Unknown error"
          );
        });
    } else {
      setLoading(true);
      axios
        .post(`${API_URL}api/add-product/${selectedBusiness}/`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          withCredentials: true,
        })
        .then((response) => {
          alert("Product added successfully!");
          resetProductForm();
          fetchProducts(selectedBusiness);
          setLoading(false);
          showToast({
            message: "Product added successfully!",
            type: "success",
            duration: 5000, // 5 seconds
          });
        })
        .catch((err) => {
          console.error(
            "Error adding product:",
            err.response ? err.response.data : "Unknown error"
          );
        });
    }
  };

  // Helper function to reset the form
  const resetProductForm = () => {
    setProductData({
      name: "",
      description: "",
      price: "",
      condition: "new",
      category: "Eco-Friendly Business",
      product_category: "",
      images: [],
      existingImages: [],
      variants: [],
    });
  };

  // Function to initialize the form with existing product data
  const initEditProduct = (productId) => {
    const productToEdit = products.find((p) => p.id === productId);

    if (!productToEdit) {
      console.error("Product not found!");
      return;
    }

    // Initialize the product data with existing values
    setProductData({
      name: productToEdit.name || "",
      description: productToEdit.description || "",
      price: productToEdit.price || "",
      condition: productToEdit.condition || "new",
      category: productToEdit.category || "Eco-Friendly Business",
      product_category: productToEdit.product_category || "",
      images: [], // New images to upload
      existingImages: productToEdit.images || [], // Existing images from the server
      variants: (productToEdit.variants || []).map((variant) => ({
        id: variant.id,
        option1: variant.option1 || "",
        option2: variant.option2 || "",
        price: variant.price || "",
        images: [], // New variant images to upload
        existingImages: variant.images || [], // Existing variant images
      })),
    });

    setEditProductId(productId);
    setIsEditFormVisible(true);
  };
  //OLD
  // const handleVariantImageChange = (e, index) => {
  //     const updatedVariants = [...productData.variants];
  //     updatedVariants[index] = { ...updatedVariants[index], image: e.target.files[0] };
  //     setProductData(prevData => ({
  //         ...prevData,
  //         variants: updatedVariants
  //     }));
  // };

  // ORIGINAL WORKING
  // const handleProductSubmit = (e) => {
  //     e.preventDefault();

  //     if (!selectedBusiness) {
  //         alert('Please select a business before adding or editing a product.');
  //         return;
  //     }

  //     const formData = new FormData();
  //     formData.append('name', productData.name);
  //     formData.append('description', productData.description);
  //     formData.append('price', Number(productData.price));  // Ensure it's a number
  //     formData.append('condition', productData.condition);
  //     formData.append('category', productData.category); // Always Eco-Friendly Business
  //     console.log(productData.image);
  //     formData.append('image', productData.image);

  //     // Append product image only if it exists
  //     // if (productData.image) {
  //     //     formData.append('image', productData.image);
  //     // }

  //     // Add variants properly
  //     productData.variants.forEach((variant, index) => {
  //         formData.append(`variants[${index}].option1`, variant.option1);
  //         formData.append(`variants[${index}].price`, variant.price);
  //         if (variant.option2) {
  //             formData.append(`variants[${index}].option2`, variant.option2);
  //         }
  //         if (variant.image) {
  //             formData.append(`variants[${index}].image`, variant.image);
  //         }
  //     });
  //     // Add or update product for the selected business
  //     if (editProductId) {
  //         // Update product
  //         axios.put(`http://127.0.0.1:8000/api/products/${editProductId}/edit/`, formData, { withCredentials: true })
  //             .then(response => {
  //                 alert('Product updated successfully!');
  //                 fetchProducts(selectedBusiness);
  //                 setIsEditFormVisible(false);
  //                 setEditProductId(null);
  //                 setProductData({
  //                     name: '',
  //                     description: '',
  //                     price: '',
  //                     condition: 'new',
  //                     category: 'Eco-Friendly Business',
  //                     image: null,
  //                     variants: []
  //                 });
  //             })
  //             .catch(err => {
  //                 console.error("Error updating product:", err.response ? err.response.data : 'Unknown error');
  //             });
  //     } else {
  //         // Add new product
  //         formData.forEach((value, key) => {
  //             console.log(key, value);
  //         });

  //         axios.post(`http://127.0.0.1:8000/api/add-product/${selectedBusiness}/`, formData, { withCredentials: true })
  //             .then(response => {
  //                 alert('Product added successfully!');
  //                 setProductData({
  //                     name: '',
  //                     description: '',
  //                     price: '',
  //                     condition: 'new', // Reset to "New"
  //                     category: 'Eco-Friendly Business', // Fixed category
  //                     image: null,
  //                     variants: [] // Clear variants
  //                 });
  //                 fetchProducts(selectedBusiness);
  //             })
  //             .catch(err => {
  //                 console.error("Error adding product:", err.response ? err.response.data : 'Unknown error');
  //             });
  //     }
  // };

  // WORKING APR 16th
  // const handleProductSubmit = (e) => {
  //     e.preventDefault();

  //     if (!selectedBusiness) {
  //         alert('Please select a business before adding or editing a product.');
  //         return;
  //     }

  //     const formData = new FormData();
  //     formData.append('name', productData.name);
  //     formData.append('description', productData.description);
  //     formData.append('price', Number(productData.price));
  //     formData.append('condition', productData.condition);
  //     formData.append('category', productData.category);
  //     formData.append('product_category', productData.product_category);
  //     console.log(productData.image);
  //     if (productData.image instanceof File) {
  //         formData.append('image', productData.image);
  //     }
  //     //formData.append('image', productData.image);

  //     productData.variants.forEach((variant, index) => {
  //         formData.append(`variants[${index}].option1`, variant.option1);
  //         formData.append(`variants[${index}].price`, variant.price);
  //         if (variant.option2) {
  //             formData.append(`variants[${index}].option2`, variant.option2);
  //         }
  //         if (variant.image) {
  //             formData.append(`variants[${index}].image`, variant.image);
  //         }
  //     });

  //     if (editProductId) {
  //         axios.put(
  //             `${API_URL}api/products/${editProductId}/edit/`,
  //             formData,
  //             {
  //                 headers: {
  //                     Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  //                 },
  //                 withCredentials: true,
  //             }
  //         )
  //             .then(response => {
  //                 alert('Product updated successfully!');
  //                 fetchProducts(selectedBusiness);
  //                 setIsEditFormVisible(false);
  //                 setEditProductId(null);
  //                 setProductData({
  //                     name: '',
  //                     description: '',
  //                     price: '',
  //                     condition: 'new',
  //                     category: 'Eco-Friendly Business',
  //                     image: null,
  //                     variants: []
  //                 });
  //             })
  //             .catch(err => {
  //                 console.error("Error updating product:", err.response ? err.response.data : 'Unknown error');
  //             });
  //     } else {
  //         formData.forEach((value, key) => {
  //             console.log(key, value);
  //         });

  //         axios.post(
  //             `${API_URL}api/add-product/${selectedBusiness}/`,
  //             formData,
  //             {
  //                 headers: {
  //                     Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  //                 },
  //                 withCredentials: true,
  //             }
  //         )
  //             .then(response => {
  //                 alert('Product added successfully!');
  //                 setProductData({
  //                     name: '',
  //                     description: '',
  //                     price: '',
  //                     condition: 'new',
  //                     category: 'Eco-Friendly Business',
  //                     image: null,
  //                     variants: []
  //                 });
  //                 fetchProducts(selectedBusiness);
  //             })
  //             .catch(err => {
  //                 console.error("Error adding product:", err.response ? err.response.data : 'Unknown error');
  //             });
  //     }
  // };

  const handleDeleteProduct = (productId) => {
    setDeleteConfirmation({
      show: true,
      productId: productId,
    });
  };

  const confirmDelete = () => {
    if (deleteConfirmation.productId) {
      axios
        .delete(
          `${API_URL}api/products/${deleteConfirmation.productId}/delete/`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
            withCredentials: true,
          }
        )
        .then(() => {
          setProducts(
            products.filter(
              (product) => product.id !== deleteConfirmation.productId
            )
          );
          setDeleteConfirmation({ show: false, productId: null });
          showToast({
            message: "Product deleted successfully!",
            type: "success",
            duration: 5000, // 5 seconds
          });
        })
        .catch((err) => {
          console.error(
            "Error deleting product:",
            err.response ? err.response.data : "Unknown error"
          );
          setDeleteConfirmation({ show: false, productId: null });
        });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ show: false, productId: null });
  };

  // const handleEditButtonClick = (productId) => {
  //     const product = products.find(p => p.id === productId);
  //     setProductData({
  //         name: product.name,
  //         description: product.description,
  //         price: product.price,
  //         condition: product.condition,
  //         category: product.category,
  //         image: null // Assuming image can't be updated directly for now
  //     });
  //     setEditProductId(productId);
  //     setIsEditFormVisible(true);
  // };

  const handleEditButtonClick = (productId) => {
    const product = products.find((p) => p.id === productId);

    setProductData({
      name: product.name,
      description: product.description,
      price: product.price,
      condition: product.condition,
      category: product.category,
      image: product.image,
      variants: product.variants
        ? product.variants.map((variant) => ({
            option1: variant.option1,
            option2: variant.option2 || "",
            price: variant.price,
            image: null, // Reset image since file inputs can't be pre-filled
          }))
        : [],
    });

    setEditProductId(productId);
    setIsEditFormVisible(true);
  };

  // const handleShippingFees = () => {
  //     const token = localStorage.getItem("access_token");
  //     const businessId = selectedBusiness;

  //     axios.post(`${API_URL}api/shipping-fees/${businessId}/`, newFees, {
  //         headers: {
  //             Authorization: `Bearer ${token}`,
  //         },
  //     })
  //     .then((response) => {
  //         console.log("Shipping fees updated successfully");
  //     })
  //     .catch((err) => {
  //         console.error("Error updating shipping fees:", err);
  //     });
  // };

  const handleShippingFees = () => {
    const token = localStorage.getItem("access_token");
    const businessId = selectedBusiness;

    const payload = {
      worldwide_shipping_fee: newFees.worldwide_shipping_fee
        ? parseFloat(newFees.worldwide_shipping_fee)
        : null,
      all_except_selected_fee: newFees.all_except_selected_fee
        ? parseFloat(newFees.all_except_selected_fee)
        : null,
      threshold_free: newFees.threshold_free
        ? parseFloat(newFees.threshold_free)
        : null,
      country_fees: newFees.country_fees.map((fee) => ({
        country: fee.country_code,
        fee: parseFloat(fee.country_fee),
      })),
    };

    axios
      .post(`${API_URL}api/shipping-fees/${businessId}/`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        // console.log("Shipping fees updated successfully");
        showToast({
          message: "Shipping fees saved!",
          type: "success",
          duration: 5000, // 5 seconds
        });
        getShippingFees();
        setNewFees((prev) => ({
          ...prev,
          country_fees: [],
        }));
      })
      .catch((err) => {
        console.error("Error updating shipping fees:", err);
      });
  };

  const getShippingFees = () => {
    const token = localStorage.getItem("access_token");
    const businessId = selectedBusiness;

    axios
      .get(`${API_URL}api/shipping-fees/${businessId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setShippingFees(response.data);
        setNewFees((prev) => ({
          ...prev,
          worldwide_shipping_fee: response.data.worldwide_shipping_fee || "",
          all_except_selected_fee: response.data.all_except_selected_fee || "",
          threshold_free: response.data.threshold_free || "",
          country_fees: [], // Clear this when reloading
        }));
      })
      .catch((err) => {
        console.error("Error fetching shipping fees:", err);
      });
  };

  // Fetch shipping fees when logistics view is active
  useEffect(() => {
    if (activeView === "logistics") {
      getShippingFees();
    }
  }, [activeView, selectedBusiness]);

  //TESTING THE SHOPIFY CONNECT
  // const handleConnectShopify = async (e) => {
  //     e.preventDefault();

  //     if (!selectedBusiness) {
  //         alert('Please select a business before connecting to Shopify.');
  //         return;
  //     }
  //     console.log("Selected Business ID:", selectedBusiness); // Log the business ID
  //     console.log("Shopify URL:", shopifyUrl); // Log the Shopify URL

  //     try {
  //         const response = await axios.post(
  //             `http://127.0.0.1:8000/api/shopify/connect/`,
  //             JSON.stringify({
  //                 shop_url: shopifyUrl,
  //                 business_id: selectedBusiness
  //             }),
  //             {
  //                 headers: {
  //                     'Content-Type': 'application/json'
  //                 },
  //                 withCredentials: true
  //             }
  //         );

  //         console.log('Response:', response);

  //         if (response.data.oauth_url) {
  //             // Redirect to OAuth link
  //             window.open(response.data.oauth_url, '_blank');
  //             // window.location.href = response.data.oauth_url;
  //         } else {
  //             alert('Failed to generate OAuth link.');
  //         }
  //     } catch (err) {
  //         console.error("Error connecting to Shopify:", err.response ? err.response.data : 'Unknown error');
  //         alert("Failed to connect to Shopify.");
  //     }
  // };

  const handleConnectShopify = async (e) => {
    e.preventDefault();

    if (!selectedBusiness) {
      alert("Please select a business before connecting to Shopify.");
      return;
    }
    // console.log("Selected Business ID:", selectedBusiness);
    // console.log("Shopify URL:", shopifyUrl);

    try {
      const response = await axios.post(
        `${API_URL}api/shopify/connect/`,
        JSON.stringify({
          shop_url: shopifyUrl,
          business_id: selectedBusiness,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`, // Add Authorization header with Bearer token
          },
          withCredentials: true,
        }
      );

      // console.log("Response:", response);

      if (response.data.oauth_url) {
        window.open(response.data.oauth_url, "_blank");
      } else {
        alert("Failed to generate OAuth link.");
      }
    } catch (err) {
      console.error(
        "Error connecting to Shopify:",
        err.response ? err.response.data : "Unknown error"
      );
      alert("Failed to connect to Shopify.");
    }
  };

  //manual one for now
  const handleManualShopifyConnect = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(
        `${API_URL}api/shopifymanualconnect/${selectedBusinessDetails.id}/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            access_token: manualAccessToken,
            shopify_store_url: manualShopifyUrl,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert("Successfully connected to Shopify!");
        //window.location.reload();
        handleBusinessSelect(selectedBusinessDetails.id);
        setActiveView("shopify");
        // Optionally refresh business data
      } else {
        alert(data.error || "Failed to connect.");
      }
    } catch (error) {
      console.error("Error connecting to Shopify manually:", error);
      alert("Something went wrong.");
    }
  };

  //SHopify

  const handleToggleOrderSync = () => {
    const newStatus = !orderSyncEnabled;
    setOrderSyncEnabled(newStatus); // Optimistic UI update

    axios
      .put(
        `${API_URL}api/business/overview/${selectedBusinessDetails?.id}/`,
        { shopify_order_sync: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`, // Add Authorization header with Bearer token
          },
          withCredentials: true,
        }
      )
      .then((response) => {
        // console.log("Order sync updated successfully:", response.data);
      })
      .catch((error) => {
        console.error("Error updating order sync:", error);
        setOrderSyncEnabled(!newStatus); // Revert if error occurs
      });
  };

  // const handleShopifySyncProducts = () => {

  //     console.log(selectedBusinessDetails.id)
  //     if (!selectedBusinessDetails.id) return;
  //     const businessId = selectedBusinessDetails.id;
  //     const queryParam = clearWithSync ? "?clear_with_shopify_sync=true" : "";
  //     const url = `http://127.0.0.1:8000/api/sync-from-shopify/${businessId}/${queryParam}`;
  //     //const url = `http://127.0.0.1:8000/api/sync-from-shopify/${businessId}${queryParam}`;
  //     try {
  //         const response = axios.get(url);
  //         console.log("Sync successful:", response.data);
  //         alert("Products synced successfully!");
  //         fetchProducts(selectedBusinessDetails.id);

  //     } catch (error) {
  //         console.error("Error syncing products:", error);
  //         alert("Failed to sync products.");
  //     }

  //     setIsShopifySyncModalOpen(false); // Close modal after request
  // };

  const handleShopifySyncProducts = () => {
    // console.log(selectedBusinessDetails.id);
    if (!selectedBusinessDetails.id) return;
    const businessId = selectedBusinessDetails.id;
    const queryParam = clearWithSync ? "?clear_with_shopify_sync=true" : "";
    const url = `${API_URL}api/sync-from-shopify-opt/${businessId}/${queryParam}`;

    try {
      const response = axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`, // Add Authorization header with Bearer token
        },
      });
      // console.log("Sync successful:", response.data);
      //alert("Products synced successfully!");
      showToast({
        message: "Products synced successfully!",
        type: "success",
        duration: 5000, // 5 seconds
      });
      fetchProducts(selectedBusinessDetails.id);
    } catch (error) {
      console.error("Error syncing products:", error);
      alert("Failed to sync products.");
    }

    setIsShopifySyncModalOpen(false); // Close modal after request
  };

  const syncShippingFeesFromShopify = async () => {
    try {
      const response = await fetch(
        `${API_URL}api/sync-shopify-shipping-fees/${selectedBusinessDetails.id}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok)
        throw new Error("Failed to sync shipping fees from Shopify");

      const data = await response.json();
      getShippingFees();
      // console.log("Shipping fees synced successfully:", data);
      showToast({
        message: "Shipping fees synced successfully!",
        type: "success",
        duration: 5000, // 5 seconds
      });
      // Optionally refetch cart/business data or show toast
    } catch (error) {
      console.error("Error syncing shipping fees from Shopify:", error);
    }
  };

  // console.log("productData.image:", productData.image);
  // console.log("typeof productData.image:", typeof productData.image);
  // console.log("using just products", products);
  // console.log(editProductId);

  const handleEditBusiness = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);

    // Append business ID fields manually in case anything needs renaming
    formData.append("name", formData.get("name"));
    formData.append("description", formData.get("description"));
    formData.append("email", formData.get("email"));
    formData.append("website", formData.get("website"));
    formData.append("shipping_strategy", formData.get("shipping_strategy"));
    formData.append("price_range", formData.get("price_range"));
    formData.append("production", formData.get("production"));

    // If a new logo was selected, it's already included in formData via e.target
    // Only submit if user is editing their own business (make sure selectedBusinessDetails is correct)
    try {
      const response = await fetch(
        `${API_URL}api/business/edit/${selectedBusinessDetails.id}/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log("✅ Business updated:", result);
        alert("Business updated successfully!");
        setActiveView("dashboard");

        axios
          .get(
            `${API_URL}api/business/overview/${selectedBusinessDetails.id}/`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
              },
            }
          )
          .then((response) => {
            setSelectedBusinessDetails(response.data);
            showToast({
              message: "Business Updated successfully!",
              type: "success",
              duration: 5000, // 5 seconds
            });
          })
          .catch((err) => {
            setError(
              err.response
                ? err.response.data.error
                : "Something went wrong while fetching business overview"
            );
            setSelectedBusinessDetails(null);
          });
      } else {
        const errorData = await response.json();
        console.error("❌ Error updating business:", errorData);
        alert("Failed to update business.");
      }
    } catch (err) {
      console.error("❌ Network error:", err);
      alert("Something went wrong.");
    }
  };

  const handleStripeOnboardingGenerate = async () => {
    if (!selectedBusinessDetails?.stripe_account_id) return;

    try {
      const response = await fetch(
        `${API_URL}api/api/stripe/onboarding-link/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            account_id: selectedBusinessDetails.stripe_account_id,
          }),
        }
      );

      const data = await response.json();

      if (data.onboarding_url) {
        window.open(data.onboarding_url, "_blank");
      } else {
        alert(
          "Error creating onboarding link: " + (data.error || "Unknown error")
        );
      }
    } catch (err) {
      console.error("Failed to generate onboarding link:", err);
      alert("Failed to generate onboarding link.");
    }
  };

  if (error) {
    return <div>Error: {error}</div>;
  }
  {
    loading && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
        <Loader2 className="h-12 w-12 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="busDashboard">
      <h2 class="headingtitle">Business Dashboard</h2>
      <br></br>
      <ToastContainer />
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(4px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
            pointerEvents: "all",
          }}
        >
          <Loader2
            style={{
              width: "80px",
              height: "80px",
              color: "#22c55e", // green-600 equivalent
            }}
            className="animate-spin"
          />
        </div>
      )}

      <div className="bus-dashboard">
        {/* Sidebar */}
        <div class="bus-first">
          <div class="bus-sidebar">
            {/* <h3>Dashboard</h3> */}
            <div class="bus-sidebar-buttons">
              <button
                onClick={() => setActiveView("orders")}
                // onMouseOver={(e) => e.target.style.backgroundColor = '#edfce6'}
                // onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
              >
                View Orders
              </button>
              <button onClick={() => setActiveView("add")}>Add Products</button>

              <button onClick={() => setActiveView("products")}>
                View Products
              </button>

              {/* New Shopify Button */}
              <button onClick={() => setActiveView("shopify")}>
                Shopify Sync (Version 1!)
              </button>
              <button onClick={() => setActiveView("logistics")}>
                Business Logistics
              </button>
              <button onClick={() => setActiveView("editbiz")}>
                Edit Business
              </button>
            </div>
          </div>
          {/* Main Content */}

          <div class="bus-rightsection">
            {/* Dropdown to select business */}
            <div class="bus-selectandoverview">
              <div class="bus-selectbus">
                <h2>Business Overview</h2>
                <select
                  onChange={(e) => handleBusinessSelect(e.target.value)}
                  value={selectedBusiness || ""}
                >
                  <option value="">Select a business</option>
                  {businesses.map((business) => (
                    <option key={business.id} value={business.id}>
                      {business.name}
                    </option>
                  ))}
                </select>
                {/* <h4>Business Overview: </h4> */}
              </div>
              <div class="bus-overview">
                {selectedBusinessDetails && (
                  <div style={{ marginTop: "20px" }} class="bd-overviewinfo">
                    <div class="bd-overviewinfo-1">
                      <p>
                        <strong>Name:</strong> {selectedBusinessDetails.name}
                      </p>
                      {/* <p>
                        <strong>Description:</strong>{" "}
                        {selectedBusinessDetails.description}
                      </p> */}
                      <p>
                        <strong>Email:</strong> {selectedBusinessDetails.email}
                      </p>
                      <p>
                        <strong>Website:</strong>{" "}
                        <a
                          href={selectedBusinessDetails.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {selectedBusinessDetails.website}
                        </a>
                      </p>
                      {selectedBusinessDetails.stripe_account_id && (
                        <p>
                          <strong>Stripe: </strong>
                          <a
                            href={`https://dashboard.stripe.com/account/${selectedBusinessDetails.stripe_account_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Go to Stripe Dashboard
                          </a>
                        </p>
                      )}

                      {selectedBusinessDetails.stripe_status === "pending" && (
                        <p>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleStripeOnboardingGenerate();
                            }}
                            style={{
                              color: "red",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            Finish setting up Stripe to handle payments
                          </a>
                        </p>
                      )}

                      <p>
                        <strong>Public Link: </strong>
                        <a
                          // href={`https://verdurefrontendproj.vercel.app/businessproducts/${selectedBusinessDetails.id}`}
                          href={`https://urverdure.app/businessproducts/${selectedBusinessDetails.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Your Verdure Store
                        </a>
                      </p>
                    </div>
                    <div class="bd-overviewinfo-2">
                      {/* <p>
                        <strong>Shipping Transparency:</strong>{" "}
                        {selectedBusinessDetails.shipping_strategy}
                      </p>
                      <p>
                        <strong>Price Range:</strong>{" "}
                        {selectedBusinessDetails.price_range}
                      </p>
                      <p>
                        <strong>Production About:</strong>{" "}
                        {selectedBusinessDetails.production}
                      </p> */}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Error Display */}
            {error && <p style={{ color: "red" }}>{error}</p>}

            {/* loading was here */}

            {selectedBusiness && activeView === "add" && (
              <div className="bd-register-container">
                <h3>Add Products</h3>
                <form
                  onSubmit={handleProductSubmit}
                  className="bd-register-form"
                >
                  {/* Product Name */}
                  <div className="register-firstname">
                    <label>Name</label>
                    <input
                      type="text"
                      name="name"
                      value={productData.name}
                      onChange={handleProductChange}
                      required
                    />
                  </div>

                  {/* Product Description */}
                  <div>
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={productData.description}
                      onChange={handleProductChange}
                      required
                    />
                  </div>

                  {/* Product Price */}
                  <div>
                    <label>Price</label>
                    <input
                      type="number"
                      name="price"
                      value={productData.price}
                      onChange={handleProductChange}
                      required
                    />
                  </div>

                  {/* Product Condition */}
                  <div>
                    <label>Condition</label>
                    <select
                      name="condition"
                      value={productData.condition}
                      onChange={handleProductChange}
                      required
                      style={{ width: "420px" }}
                    >
                      <option value="new">New</option>
                      <option value="second_hand">Secondhand</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="product_category">Product Category:</label>
                    <select
                      id="product_category"
                      value={productData.product_category}
                      onChange={(e) =>
                        setProductData({
                          ...productData,
                          product_category: e.target.value,
                        })
                      }
                      required
                    >
                      <option value="">-- Select a category --</option>
                      <option value="Apparel">Apparel</option>
                      <option value="Shoes">Shoes</option>
                      <option value="Accessories">Accessories</option>
                      <option value="Home Goods">Home Goods</option>
                      <option value="Cosmetics / Skin Care">
                        Cosmetics / Skin Care
                      </option>
                      <option value="Hygiene">Hygiene</option>
                      <option value="Cleaning Products">
                        Cleaning Products
                      </option>
                      <option value="Art Supplies"> Art Supplies</option>
                    </select>
                  </div>

                  {/* Product Images - Updated for multiple images */}
                  <div className="addProd-img">
                    <label>Product Images</label>
                    <input
                      type="file"
                      name="images"
                      onChange={handleProductImageChange}
                      multiple
                    />
                    {/* Display selected images preview */}
                    <div className="image-preview-container">
                      {Array.isArray(productData.images) &&
                        productData.images.length > 0 && (
                          <div className="image-previews">
                            {productData.images.map((image, idx) => (
                              <div key={idx} className="image-preview-item">
                                {image instanceof File && (
                                  <img
                                    src={URL.createObjectURL(image)}
                                    alt={`Preview ${idx}`}
                                    className="image-preview"
                                  />
                                )}
                                <button
                                  type="button"
                                  className="remove-image"
                                  onClick={() => {
                                    const updatedImages = [
                                      ...productData.images,
                                    ];
                                    updatedImages.splice(idx, 1);
                                    setProductData({
                                      ...productData,
                                      images: updatedImages,
                                    });
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Variants Section */}
                  <div className="bd-variants">
                    <label>Variants</label>
                    {productData.variants.map((variant, index) => (
                      <div key={index} className="var">
                        <input
                          type="text"
                          name="option1"
                          value={variant.option1}
                          onChange={(e) => handleVariantChange(e, index)}
                          placeholder="Option 1"
                        />
                        <input
                          type="text"
                          name="option2"
                          value={variant.option2}
                          onChange={(e) => handleVariantChange(e, index)}
                          placeholder="Option 2"
                        />
                        <input
                          type="number"
                          name="price"
                          value={variant.price}
                          onChange={(e) => handleVariantChange(e, index)}
                          placeholder="Price"
                        />

                        {/* Updated variant image input to support multiple images */}
                        <div>
                          <input
                            type="file"
                            name="images"
                            onChange={(e) => handleVariantImageChange(e, index)}
                            multiple
                          />

                          {/* Display variant image previews */}
                          <div className="variant-image-previews">
                            {Array.isArray(variant.images) &&
                              variant.images.length > 0 && (
                                <div className="image-previews">
                                  {variant.images.map((image, imgIdx) => (
                                    <div
                                      key={imgIdx}
                                      className="image-preview-item"
                                    >
                                      {image instanceof File && (
                                        <img
                                          src={URL.createObjectURL(image)}
                                          alt={`Variant ${index} Preview ${imgIdx}`}
                                          className="image-preview"
                                        />
                                      )}
                                      <button
                                        type="button"
                                        className="remove-image"
                                        onClick={() => {
                                          const updatedVariants = [
                                            ...productData.variants,
                                          ];
                                          const updatedImages = [
                                            ...updatedVariants[index].images,
                                          ];
                                          updatedImages.splice(imgIdx, 1);
                                          updatedVariants[index].images =
                                            updatedImages;
                                          setProductData({
                                            ...productData,
                                            variants: updatedVariants,
                                          });
                                        }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                          </div>
                        </div>

                        {productData.variants.length > 0 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveVariant(index)}
                            className="var-but"
                          >
                            Remove Variant
                          </button>
                        )}
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={handleAddVariant}
                      className="var-but"
                    >
                      Add Variant
                    </button>
                  </div>

                  {/* Submit Button */}
                  <div>
                    <button type="submit" className="signIn-but">
                      Add Product
                    </button>
                  </div>
                </form>
                {loading && (
                  <div className="fixed inset-0 z-90 flex items-center justify-center bg-white/60 backdrop-blur-sm">
                    <Loader2 className="h-12 w-12 animate-spin text-green-600" />
                  </div>
                )}
              </div>
            )}
            {/* 
            {selectedBusiness && activeView === "logistics" && (
              <>
                <h3>
                  Shipping Fees:{" "}
                  {businesses.find((b) => b.id === selectedBusiness)?.name}
                </h3>
                {shippingFees ? (
                  <div>
                    <p>
                      Worldwide Shipping Fee: $
                      {shippingFees.worldwide_shipping_fee}
                    </p>
                    <p>
                      All Except Selected Countries Fee: $
                      {shippingFees.all_except_selected_fee}
                    </p>
                    <h4>Country-Specific Shipping Fees:</h4>
                    {shippingFees.country_fees.map((countryFee) => (
                      <div key={countryFee.country}>
                        <p>
                          {countryFee.country}: ${countryFee.fee}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No shipping fees configuration found for this business.</p>
                )}

                <div className="shipping-fees-form">
                  <h4>Update Shipping Fees</h4>

                  <label>Worldwide Shipping Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newFees.worldwide_shipping_fee}
                    onChange={(e) =>
                      setNewFees({
                        ...newFees,
                        worldwide_shipping_fee: e.target.value,
                      })
                    }
                  />

                  <label>All Except Selected Countries Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newFees.all_except_selected_fee}
                    onChange={(e) =>
                      setNewFees({
                        ...newFees,
                        all_except_selected_fee: e.target.value,
                      })
                    }
                  />

                  <label>Country Code</label>
                  <input
                    type="text"
                    value={newCountryFee.country_code}
                    onChange={(e) =>
                      setNewCountryFee({
                        ...newCountryFee,
                        country_code: e.target.value.toUpperCase(),
                      })
                    }
                  />

                  <label>Country Specific Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newCountryFee.country_fee}
                    onChange={(e) =>
                      setNewCountryFee({
                        ...newCountryFee,
                        country_fee: e.target.value,
                      })
                    }
                  />

                  <button
                    onClick={() => {
                      if (
                        newCountryFee.country_code &&
                        newCountryFee.country_fee
                      ) {
                        setNewFees((prev) => ({
                          ...prev,
                          country_fees: [...prev.country_fees, newCountryFee],
                        }));
                        setNewCountryFee({ country_code: "", country_fee: "" });
                      }
                    }}
                  >
                    Add Country Fee
                  </button>

                  <button onClick={handleShippingFees}>
                    Save Shipping Fees
                  </button>

                  {newFees.country_fees.length > 0 && (
                    <div>
                      <h5>Preview Country Fees:</h5>
                      <ul>
                        {newFees.country_fees.map((cf, idx) => (
                          <li key={idx}>
                            {cf.country_code}: ${cf.country_fee}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )} */}

            {selectedBusiness && activeView === "logistics" && (
              <div className="logistics-container">
                <h3>
                  Shipping Fees:{" "}
                  {businesses.find((b) => b.id === selectedBusiness)?.name}
                </h3>
                <p>
                  {" "}
                  Here you can configure shipping fees. To add a flat rate to
                  everywhere, put a number in WorldWide Shipping fees.
                </p>
                <p>
                  <b>
                    To add a shipping fee for a country specifically, put the
                    Country code (eg, US), and the amount you want, then select
                    add Country Fee. It will allow you to preview, then select
                    Save Shipping Fees.
                  </b>{" "}
                </p>
                {shippingFees ? (
                  <div className="current-fees-display">
                    <p>
                      Worldwide Shipping Fee: $
                      {shippingFees.worldwide_shipping_fee}
                    </p>
                    <p>
                      All Except Selected Countries Fee: $
                      {shippingFees.all_except_selected_fee}
                    </p>
                    <p>
                      Free Shipping Threshold: $
                      {shippingFees.threshold_free ?? "Not set"}
                    </p>
                    <h4>Country-Specific Shipping Fees:</h4>
                    {shippingFees.country_fees.map((countryFee) => (
                      <div
                        key={countryFee.country}
                        className="country-fee-item"
                      >
                        <p>
                          {countryFee.country}: ${countryFee.fee}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-fees-message">
                    No shipping fees configuration found for this business.
                  </p>
                )}

                <div className="shipping-fees-form">
                  <h4>Update Shipping Fees</h4>

                  <label>Worldwide Shipping Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newFees.worldwide_shipping_fee}
                    onChange={(e) =>
                      setNewFees({
                        ...newFees,
                        worldwide_shipping_fee: e.target.value,
                      })
                    }
                  />

                  <label>All Except Selected Countries Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newFees.all_except_selected_fee}
                    onChange={(e) =>
                      setNewFees({
                        ...newFees,
                        all_except_selected_fee: e.target.value,
                      })
                    }
                  />

                  <label>Free Shipping Threshold (Optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newFees.threshold_free}
                    onChange={(e) =>
                      setNewFees({
                        ...newFees,
                        threshold_free: e.target.value,
                      })
                    }
                  />

                  <label>Country Code</label>
                  <input
                    type="text"
                    value={newCountryFee.country_code}
                    onChange={(e) =>
                      setNewCountryFee({
                        ...newCountryFee,
                        country_code: e.target.value.toUpperCase(),
                      })
                    }
                  />

                  <label>Country Specific Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newCountryFee.country_fee}
                    onChange={(e) =>
                      setNewCountryFee({
                        ...newCountryFee,
                        country_fee: e.target.value,
                      })
                    }
                  />

                  <div className="form-buttons">
                    <button
                      onClick={() => {
                        if (
                          newCountryFee.country_code &&
                          newCountryFee.country_fee
                        ) {
                          setNewFees((prev) => ({
                            ...prev,
                            country_fees: [...prev.country_fees, newCountryFee],
                          }));
                          setNewCountryFee({
                            country_code: "",
                            country_fee: "",
                          });
                        }
                      }}
                    >
                      Add Country Fee
                    </button>

                    <button onClick={handleShippingFees}>
                      Save Shipping Fees
                    </button>
                  </div>

                  {newFees.country_fees.length > 0 && (
                    <div className="preview-country-fees">
                      <h5>Preview Country Fees:</h5>
                      <ul>
                        {newFees.country_fees.map((cf, idx) => (
                          <li key={idx}>
                            <span>{cf.country_code}</span>
                            <span>${cf.country_fee}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeView === "editbiz" && selectedBusinessDetails && (
              <div className="modal">
                <h2>Edit Business</h2>
                <form onSubmit={handleEditBusiness}>
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    defaultValue={selectedBusinessDetails.name}
                    required
                  />
                  <input
                    type="text"
                    name="description"
                    placeholder="Description"
                    defaultValue={selectedBusinessDetails.description}
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    defaultValue={selectedBusinessDetails.email}
                    required
                  />
                  <input
                    type="url"
                    name="website"
                    placeholder="Website"
                    defaultValue={selectedBusinessDetails.website}
                  />
                  <input
                    type="text"
                    name="shipping_strategy"
                    placeholder="Shipping Strategy"
                    defaultValue={selectedBusinessDetails.shipping_strategy}
                  />
                  <input
                    type="text"
                    name="price_range"
                    placeholder="Price Range"
                    defaultValue={selectedBusinessDetails.price_range}
                  />
                  <input
                    type="text"
                    name="production"
                    placeholder="Production"
                    defaultValue={selectedBusinessDetails.production}
                  />

                  <label>Upload New Logo</label>
                  <input type="file" name="logonew" accept="image/*" />

                  <label>Upload New Banner</label>
                  <input type="file" name="banner" accept="image/*" />

                  <button type="submit">Save</button>
                </form>
              </div>
            )}

            {selectedBusiness && activeView === "products" && (
              <div className="bd-productsfor">
                <h3>
                  Products for{" "}
                  {businesses.find((b) => b.id === selectedBusiness)?.name}
                </h3>
                <div>
                  {products.length === 0 ? (
                    <p>No products found for this business.</p>
                  ) : (
                    <div className="bd-productsfor-list">
                      <table>
                        <thead>
                          <tr>
                            <th>Product Name</th>
                            <th>Price</th>
                            <th>Condition</th>
                            <th>Variants</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map((product) => (
                            <tr key={product.id}>
                              <td>{product.name}</td>
                              <td>${product.price}</td>
                              <td>{product.condition}</td>
                              <td>
                                {product.variants.length === 0 ? (
                                  <span>No variants</span>
                                ) : (
                                  // <ul>
                                  //     {product.variants.map(variant => (
                                  //         <li key={variant.id}>
                                  //             {variant.option1} {variant.option2 ? ` - ${variant.option2}` : ''}
                                  //             {variant.price && ` - $${variant.price}`}
                                  //         </li>
                                  //     ))}
                                  // </ul>
                                  <ul>
                                    {product.variants
                                      .filter((variant) => !variant.deleted) // only show non-deleted variants
                                      .map((variant) => (
                                        <li key={variant.id}>
                                          {variant.option1}{" "}
                                          {variant.option2
                                            ? ` - ${variant.option2}`
                                            : ""}
                                          {variant.price &&
                                            ` - $${variant.price}`}
                                        </li>
                                      ))}
                                  </ul>
                                )}
                              </td>
                              <td>
                                <div className="bd-editdeletebutton">
                                  <button
                                    className="checkout-button"
                                    onClick={() =>
                                      handleEditButtonClick(product.id)
                                    }
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="checkout-button"
                                    onClick={() =>
                                      handleDeleteProduct(product.id)
                                    }
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeView === "shopify" && (
              <div className="shopify">
                {selectedBusinessDetails?.shopify_access_token ? (
                  <div className="shopify-connected">
                    <div className="connected-status">
                      <span>Shopify Connected</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6L9 17l-5-5"></path>
                      </svg>
                    </div>

                    <div className="store-url">
                      <strong>Store URL:</strong>{" "}
                      {selectedBusinessDetails.shopify_store_url}
                    </div>

                    <div className="sync-controls">
                      <div className="sync-toggle">
                        <span>Enable Order Sync</span>
                        <Switch
                          checked={orderSyncEnabled}
                          onChange={handleToggleOrderSync}
                          color="primary"
                        />
                        <p>
                          Note, orders will show as pending for Shopify
                          Compliance, but they are paid
                        </p>
                      </div>

                      <button
                        className="sync-button"
                        onClick={() => setIsShopifySyncModalOpen(true)}
                      >
                        Sync Products
                      </button>

                      <button
                        className="sync-button"
                        onClick={() => syncShippingFeesFromShopify()}
                      >
                        Sync Shipping Fees from Shopify
                      </button>
                    </div>

                    {/* Modal */}
                    {isShopifySyncModalOpen && (
                      <div className="modal">
                        <div className="modal-content">
                          <h3>Sync Products with Shopify</h3>

                          <div className="checkbox-container">
                            <input
                              type="checkbox"
                              id="clearWithSync"
                              checked={clearWithSync}
                              onChange={(e) =>
                                setClearWithSync(e.target.checked)
                              }
                            />
                            <label htmlFor="clearWithSync">
                              Clear existing products before syncing?
                            </label>
                          </div>

                          <div className="modal-buttons">
                            <button onClick={() => handleShopifySyncProducts()}>
                              Confirm
                            </button>
                            <button
                              onClick={() => setIsShopifySyncModalOpen(false)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="manual-shopify-setup">
                    <h3>Manual Shopify Connect</h3>

                    <p>
                      Hi! We are working hard on getting the Shopify Sync in.
                      Our goal is to create an app on the Shopify app store for
                      you to be able to easily install the Verdure sync. We are
                      working to ensure that this app can be published in the
                      next week or so.
                    </p>

                    <p>
                      However, if you'd still like to sync with Shopify, we can
                      do so another way, by setting up a custom app on your
                      admin dashboard (around 5+ mins), and securely providing
                      our platform the information necessary.
                    </p>

                    <ol className="instructions">
                      {[
                        "Navigate to your Admin Dashboard of Shopify",
                        "Click the arrow next to apps on the left sidebar, and click **Apps and Sales Channel Settings**. This will pop up a new window.",
                        "Click the button **Develop Apps**, then click **Create an App**, and name it Verdure.",
                        "Under **Overview**, click **Configure Admin API Scopes**, and search/check the following: `read_orders`, `write_orders`, `read_products`, `write_products`, `read_shipping`. Hit save.",
                        "Once done, go to **API Credentials** and hit **Install App**!",
                        "Copy the generated access token.",
                        "Come back to your Verdure dashboard and paste your token and store URL (e.g., `yourstore.myshopify.com`) below.",
                        "Hit save, and you're good to go!",
                      ].map((step, index) => (
                        <div
                          key={index}
                          style={{ display: "flex", marginBottom: "10px" }}
                        >
                          <div style={{ minWidth: "30px", fontWeight: "bold" }}>
                            {index + 1}.
                          </div>
                          <div>{step}</div>
                        </div>
                      ))}
                    </ol>

                    <form
                      onSubmit={handleManualShopifyConnect}
                      className="shopify-connect-form"
                    >
                      <input
                        className="shopify-input"
                        type="text"
                        placeholder="Shopify Store URL (e.g. yourstore.myshopify.com)"
                        value={manualShopifyUrl}
                        onChange={(e) => setManualShopifyUrl(e.target.value)}
                      />
                      <input
                        className="shopify-input"
                        type="text"
                        placeholder="Access Token"
                        value={manualAccessToken}
                        onChange={(e) => setManualAccessToken(e.target.value)}
                      />
                      <button type="submit" className="connect-button">
                        Submit Token & URL
                      </button>
                    </form>

                    {/* Might need to comment the following out */}
                    {/* <h1>COMING SOON: SHOPIFY SYNC APP FOR APP STORE! Please follow the instructions above :D</h1>
                    <form onSubmit={handleConnectShopify} class="shopify-connect">
                      <input
                        type="text"
                        placeholder="Enter your Shopify store URL (e.g., testingstoreverdure.myshopify.com)"
                        value={shopifyUrl}
                        onChange={(e) => setShopifyUrl(e.target.value)}
                      />
                      <button type="submit" class="signIn-but">
                        Connect
                      </button>
                    </form> */}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div class="bus-second">
          {/* Opder */}
          {/* {selectedBusiness && activeView === 'orders' && (
                        <div class="bd-orderssection">
                            <h3>Orders for {businesses.find(b => b.id === selectedBusiness)?.name}</h3>
                            {orders.length === 0 ? (
                                <p>No orders found for this business.</p>
                            ) : (
                                <table class="bd-tablelayout">
                                    <thead class="bd-table-head" >
                                        <tr class="bd-table-row">
                                            <th width='50px'>#</th>
                                            <th>Ordered By</th>
                                            <th class="bs-table-email-section">Email</th>
                                            <th>Total Price</th>
                                            <th width='100px'>Status</th>
                                            <th>Shipping Address</th>
                                            <th>Tracking #</th>
                                            <th>Notes for Customer</th>
                                            <th>Date Placed (UTC)</th>
                                            <th width='200px'>Order Items</th>
                                            <th width='100px'>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody class="bd-table">
                                        {orders
                                            .sort((a, b) => b.id - a.id) // Sort orders by ID in descending order
                                            .map(order => {
                                                const shippingAddress = order.shipping_address ? JSON.parse(order.shipping_address) : {};
                                                return (
                                                    <tr key={order.id}>
                                                        <td>{order.id}</td>
                                                        <td>{order.user_full_name}</td>
                                                        <td class="bs-table-email-section">{order.user_email}</td>
                                                        <td>${order.total_price}</td>
                                                        <td>{order.status}</td>
                                                        <td class="bd-table-ship">
                                                            {shippingAddress && (
                                                                <>
                                                                    {shippingAddress.line1}, {shippingAddress.line2} <br />
                                                                    {shippingAddress.city}, {shippingAddress.state} <br />
                                                                    {shippingAddress.postal_code}, {shippingAddress.country}
                                                                </>
                                                            )}
                                                        </td>
                                                        <td>{order.tracking_number}</td>
                                                        <td>{order.notes}</td>
                                                        <td>{new Date(order.created_at).toLocaleString()}</td>
                                                        <td>
                                                            {order.order_items.map(item => (
                                                                <div key={item.id}>
                                                                    {item.product_name} ({item.product_price}) x {item.quantity}
                                                                </div>
                                                            ))}
                                                        </td>
                                                        <td class="bd-table-button">
                                                            <button onClick={() => openOrderEditModal(order)} class="bd-table-but">Edit Order</button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )} */}
          {selectedBusiness && activeView === "orders" && (
            <div className="bd-orderssection">
              <h3>
                Orders for{" "}
                {businesses.find((b) => b.id === selectedBusiness)?.name}
              </h3>
              {orders.length === 0 ? (
                <p>No orders found for this business.</p>
              ) : (
                <table className="bd-tablelayout">
                  <thead className="bd-table-head">
                    <tr className="bd-table-row">
                      <th width="50px">#</th>
                      <th>Ordered By</th>
                      <th className="bs-table-email-section">Email</th>
                      <th>Total Price</th>
                      <th width="100px">Status</th>
                      <th>Shipping Address</th>
                      <th>Tracking #</th>
                      <th>Notes for Customer</th>
                      <th>Date Placed (UTC)</th>
                      <th width="200px">Order Items</th>
                      <th width="100px">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bd-table">
                    {orders
                      .sort((a, b) => b.id - a.id) // Sort orders by ID in descending order
                      .map((order) => {
                        const shippingAddress = order.shipping_address
                          ? JSON.parse(order.shipping_address)
                          : {};
                        return (
                          <tr key={order.id}>
                            <td id="bd-table-orderid">{order.id}</td>
                            <td id="bd-table-name">{order.user_full_name}</td>
                            <td className="bs-table-email-section">
                              {order.user_email}
                            </td>
                            <td>${order.total_price}</td>
                            <td>{order.status}</td>
                            <td className="bd-table-ship">
                              {shippingAddress && (
                                <>
                                  {shippingAddress.line1},{" "}
                                  {shippingAddress.line2} <br />
                                  {shippingAddress.city},{" "}
                                  {shippingAddress.state} <br />
                                  {shippingAddress.postal_code},{" "}
                                  {shippingAddress.country}
                                </>
                              )}
                            </td>
                            <td>{order.tracking_number}</td>
                            <td>{order.notes}</td>
                            <td>
                              {new Date(order.created_at).toLocaleString()}
                            </td>
                            <td>
                              {order.order_items.map((item) => (
                                <div key={item.id}>
                                  {/* Display product name and price */}
                                  <div>
                                    {item.product_name} (${item.product_price})
                                    x {item.quantity}
                                  </div>

                                  {/* If the product has variants, show them as sub-bullet points */}
                                  {item.variant_options &&
                                    Object.keys(item.variant_options).length >
                                      0 && (
                                      <ul>
                                        {Object.entries(
                                          item.variant_options
                                        ).map(([option, value], index) => (
                                          <li key={index}>
                                            {option}: {value}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                </div>
                              ))}
                            </td>
                            <td className="bd-table-button">
                              <button
                                onClick={() => openOrderEditModal(order)}
                                className="bd-table-but"
                              >
                                Edit Order
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
        {/* Edit Product Modal */}
        {isEditFormVisible && (
          <div
            style={{
              position: "fixed",
              top: "0",
              left: "0",
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                background: "white",
                padding: "20px",
                borderRadius: "8px",
                maxWidth: "800px",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <h3>Edit Product</h3>
              <form onSubmit={handleProductSubmit}>
                <div>
                  <label>Name</label>
                  <input
                    type="text"
                    name="name"
                    value={productData.name}
                    onChange={handleProductChange}
                    required
                  />
                </div>
                <div>
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={productData.description}
                    onChange={handleProductChange}
                    required
                  />
                </div>
                <div>
                  <label>Price</label>
                  <input
                    type="number"
                    name="price"
                    value={productData.price}
                    onChange={handleProductChange}
                    required
                  />
                </div>
                <div>
                  <label>Condition</label>
                  <select
                    name="condition"
                    value={productData.condition}
                    onChange={handleProductChange}
                    required
                  >
                    <option value="new">New</option>
                    <option value="second_hand">Secondhand</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="product_category">
                    Product Category (Required):
                  </label>
                  <select
                    id="product_category"
                    value={productData.product_category}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        product_category: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">-- Select a category --</option>
                    <option value="Apparel">Apparel</option>
                    <option value="Shoes">Shoes</option>
                    <option value="Accessories">Accessories</option>
                    <option value="Home Goods">Home Goods</option>
                    <option value="Cosmetics / Skin Care">
                      Cosmetics / Skin Care
                    </option>
                    <option value="Hygiene">Hygiene</option>
                    <option value="Cleaning Products">Cleaning Products</option>
                    <option value="Art Supplies"> Art Supplies</option>
                  </select>
                </div>

                {/* Product Images Section */}
                <div style={{ marginBottom: "20px" }}>
                  <label>Product Images</label>

                  {/* Display existing images */}
                  {productData.existingImages &&
                    productData.existingImages.length > 0 && (
                      <div style={{ marginBottom: "10px" }}>
                        <p>Current Images:</p>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10px",
                          }}
                        >
                          {productData.existingImages.map((img, index) => (
                            <div
                              key={`existing-${index}`}
                              style={{ position: "relative", width: "100px" }}
                            >
                              <img
                                src={img.image}
                                alt={
                                  img.alt_text || `Product image ${index + 1}`
                                }
                                style={{
                                  width: "100%",
                                  height: "80px",
                                  objectFit: "cover",
                                }}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveProductImage(index, true)
                                }
                                style={{
                                  position: "absolute",
                                  top: "0",
                                  right: "0",
                                  background: "red",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "50%",
                                  width: "20px",
                                  height: "20px",
                                  cursor: "pointer",
                                }}
                              >
                                ×
                              </button>
                              <small>{img.image.split("/").pop()}</small>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Display newly selected files */}
                  {productData.images && productData.images.length > 0 && (
                    <div style={{ marginBottom: "10px" }}>
                      <p>New Images to Upload:</p>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "10px",
                        }}
                      >
                        {productData.images.map((file, index) => (
                          <div
                            key={`new-${index}`}
                            style={{ position: "relative", width: "100px" }}
                          >
                            <div
                              style={{
                                width: "100%",
                                height: "80px",
                                background: "#f0f0f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <span>{file.name}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                handleRemoveProductImage(index, false)
                              }
                              style={{
                                position: "absolute",
                                top: "0",
                                right: "0",
                                background: "red",
                                color: "white",
                                border: "none",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer",
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <input
                    type="file"
                    name="images"
                    onChange={handleProductImageChange}
                    multiple
                  />
                  <p>
                    <small>
                      Select multiple files to upload additional product images
                    </small>
                  </p>

                  <div className="form-group">
                    <div className="checkbox-wrapper">
                      <input
                        type="checkbox"
                        id="clearProductImages"
                        checked={productData.clearImages || false}
                        onChange={(e) =>
                          setProductData({
                            ...productData,
                            clearImages: e.target.checked,
                          })
                        }
                      />
                      <label htmlFor="clearProductImages">
                        Clear all product images
                      </label>
                    </div>
                  </div>
                </div>

                {/* Variants Section */}
                <div>
                  <h4>Variants</h4>
                  {productData.variants.map((variant, variantIndex) => (
                    <div
                      key={variantIndex}
                      style={{
                        border: "1px solid #ddd",
                        padding: "10px",
                        marginBottom: "15px",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "10px",
                          marginBottom: "10px",
                        }}
                      >
                        <div>
                          <label>Option 1:</label>
                          <input
                            type="text"
                            name="option1"
                            placeholder="Option 1"
                            value={variant.option1}
                            onChange={(e) =>
                              handleVariantChange(e, variantIndex)
                            }
                          />
                        </div>
                        <div>
                          <label>Option 2 (optional):</label>
                          <input
                            type="text"
                            name="option2"
                            placeholder="Option 2 (optional)"
                            value={variant.option2 || ""}
                            onChange={(e) =>
                              handleVariantChange(e, variantIndex)
                            }
                          />
                        </div>
                        <div>
                          <label>Price:</label>
                          <input
                            type="number"
                            name="price"
                            placeholder="Price"
                            value={variant.price}
                            onChange={(e) =>
                              handleVariantChange(e, variantIndex)
                            }
                          />
                        </div>
                      </div>

                      {/* Variant Images Section */}
                      <div style={{ marginBottom: "10px" }}>
                        <label>Variant Images</label>

                        {/* Display existing variant images */}
                        {variant.existingImages &&
                          variant.existingImages.length > 0 && (
                            <div style={{ marginBottom: "10px" }}>
                              <p>Current Variant Images:</p>
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "10px",
                                }}
                              >
                                {variant.existingImages.map((img, imgIndex) => (
                                  <div
                                    key={`var-existing-${imgIndex}`}
                                    style={{
                                      position: "relative",
                                      width: "100px",
                                    }}
                                  >
                                    <img
                                      src={img.image}
                                      alt={
                                        img.alt_text ||
                                        `Variant image ${imgIndex + 1}`
                                      }
                                      style={{
                                        width: "100%",
                                        height: "80px",
                                        objectFit: "cover",
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveVariantImage(
                                          variantIndex,
                                          imgIndex,
                                          true
                                        )
                                      }
                                      style={{
                                        position: "absolute",
                                        top: "0",
                                        right: "0",
                                        background: "red",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "50%",
                                        width: "20px",
                                        height: "20px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      ×
                                    </button>
                                    <small>{img.image.split("/").pop()}</small>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                        {/* Display newly selected variant files */}
                        {variant.images && variant.images.length > 0 && (
                          <div style={{ marginBottom: "10px" }}>
                            <p>New Variant Images to Upload:</p>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "10px",
                              }}
                            >
                              {variant.images.map((file, imgIndex) => (
                                <div
                                  key={`var-new-${imgIndex}`}
                                  style={{
                                    position: "relative",
                                    width: "100px",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "80px",
                                      background: "#f0f0f0",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <span>{file.name}</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveVariantImage(
                                        variantIndex,
                                        imgIndex,
                                        false
                                      )
                                    }
                                    style={{
                                      position: "absolute",
                                      top: "0",
                                      right: "0",
                                      background: "red",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "50%",
                                      width: "20px",
                                      height: "20px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <input
                          type="file"
                          name="variantImages"
                          onChange={(e) =>
                            handleVariantImageChange(e, variantIndex)
                          }
                          multiple
                        />
                        <p>
                          <small>
                            Select multiple files to upload additional variant
                            images
                          </small>
                        </p>
                      </div>

                      <div className="form-group">
                        <div className="checkbox-wrapper">
                          <input
                            type="checkbox"
                            id={`clearVariantImages-${variantIndex}`}
                            checked={
                              productData.variants[variantIndex]?.clearImages ||
                              false
                            }
                            onChange={(e) => {
                              const updatedVariants = [...productData.variants];
                              updatedVariants[variantIndex].clearImages =
                                e.target.checked;
                              setProductData({
                                ...productData,
                                variants: updatedVariants,
                              });
                            }}
                          />
                          <label htmlFor={`clearVariantImages-${variantIndex}`}>
                            Clear all variant images
                          </label>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveVariant(variantIndex)}
                        style={{
                          background: "#ff4d4d",
                          color: "white",
                          border: "none",
                          padding: "5px 10px",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Remove Variant
                      </button>
                    </div>
                  ))}
                  <button
                    className="var-but"
                    type="button"
                    onClick={handleAddVariant}
                    style={{
                      background: "#4CAF50",
                      color: "white",
                      border: "none",
                      padding: "8px 15px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      marginTop: "10px",
                    }}
                  >
                    Add Variant
                  </button>
                </div>

                <div
                  style={{ marginTop: "20px", display: "flex", gap: "10px" }}
                >
                  <button
                    type="submit"
                    style={{
                      background: "#2196F3",
                      color: "white",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditFormVisible(false)}
                    style={{
                      background: "#f0f0f0",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmation.show && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "10px",
                textAlign: "center",
              }}
            >
              <h3>Are you sure you want to delete this product?</h3>
              <button onClick={confirmDelete}>OK</button>
              <button onClick={cancelDelete}>Cancel</button>
            </div>
          </div>
        )}

        {isOrderEditModalOpen && selectedOrder && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)", // semi-transparent background
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000, // Ensures modal is on top of other content
            }}
          >
            <div class="bd-editorder">
              <h2>Edit Order</h2>
              <div class="bd-editorder-form">
                <form onSubmit={handleOrderEditSubmit}>
                  <div class="bd-editorder-form">
                    <label>Status</label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      {statuses.map((statusOption) => (
                        <option key={statusOption} value={statusOption}>
                          {statusOption}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div class="bd-editorder-form">
                    <label>Tracking Number</label>
                    <input
                      type="text"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                    />
                  </div>
                  <div class="bd-editorder-form">
                    <label>Notes</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                  <div class="bd-editorder-but">
                    <button type="submit" class="signIn-but">
                      Save Changes
                    </button>
                    <button
                      type="button"
                      class="signIn-but"
                      onClick={closeOrderEditModal}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Help Button */}
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: 1000,
        }}
      >
        <button
          onClick={() => setShowHelpModal(true)}
          style={{
            backgroundColor: "#22c55e",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "50px",
            height: "50px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <HelpCircle size={30} />
        </button>
      </div>

      {/* Help Modal */}
      {showHelpModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1001,
          }}
          onClick={() => setShowHelpModal(false)} // Close modal when clicking on backdrop
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "20px",
              maxWidth: "600px",
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()} // Prevent modal from closing when clicking inside content
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
                borderBottom: "1px solid #eee",
                paddingBottom: "10px",
              }}
            >
              <h2 style={{ margin: 0 }}>Dashboard Help</h2>
              <button
                onClick={() => setShowHelpModal(false)}
                style={{
                  background: "#f44336",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  padding: "8px 12px",
                  fontSize: "16px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
                aria-label="Close help modal"
              >
                Close
              </button>
            </div>

            <div>
              <h3>Business Overview</h3>
              <p>
                Select your business from the dropdown menu to view and manage
                its details, products, and orders.
              </p>
              <h3>Stripe - What is it?? Why??</h3>
              <p>
                Stripe is how Verdure handles payments securely. We will be
                making smoother integrations.
              </p>
              <p>
                The Stripe Dashboard is where you can see your funds. The amount
                that shows up has the 3% comission fee deducted already.
              </p>
              <p>
                When you set up your dashboard, you configured a bank account
                for payouts. This can always be changed too.
              </p>

              <h3>View Orders</h3>
              <p>
                Track all orders placed through your Verdure store. You can
                update order status, add tracking numbers, and add notes for
                customers.
              </p>

              <h3>Add Products</h3>
              <p>
                Add new products to your store with details like name,
                description, price, condition, and category. Add multiple
                product images and create variants with different options.
              </p>

              <h3>View Products</h3>
              <p>
                See all your listed products, edit product details or remove
                products from your store.
              </p>

              <h3>Shopify Sync</h3>
              <p>
                Connect your Shopify store to sync products between Verdure and
                Shopify. Follow the steps, and once you have completed it, you
                can hit the button to sync your products or toggle order sync to
                Shopify Dashboard.
              </p>

              <h3>Business Logistics</h3>
              <p>
                Set up and manage your shipping fees for different regions.
                Configure worldwide shipping rates and country-specific fees. If
                you use Shopify, you can pull the shipping fees from Shopify
                using the Shopify Sync.
              </p>

              <h3>Edit Business</h3>
              <p>
                Update your business information including name, description,
                contact details, and upload a new logo or banner.
              </p>

              <h3>Need More Help? Something seem off?</h3>
              <p>
                Contact Verdure support at{" "}
                <a href="mailto:yourverdure@gmail.com">yourverdure@gmail.com</a>{" "}
                for additional assistance, or call Sanjana at 4255539474
              </p>

              <p>We appreciate you as students looking to make a change!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDashboard;
